import { supabase } from '@/integrations/supabase/client';
import { normalizeDomainInput } from '@/lib/domainNormalization';

type SupplierDomainRow = {
  domain: string | null;
  marketplace_listing_id: string;
};

type MarketplaceListingRow = {
  id: string;
  domain: string | null;
  status?: string | null;
  company_name?: string;
  logo_url?: string | null;
  /** Row-scoped canonical computed projection when explicitly selected. */
  display_logo_url?: string | null;
  website_url?: string | null;
  service_description?: string | null;
  supplier_type?: string | null;
  country?: string | null;
  confirmed_country_code?: string | null;
  // @canonical-score-exception Logo provenance only; canonicalLogo reads branding.selected_logo.
  discovery_data?: unknown;
};

type VendorProfileRow = {
  id: string;
  marketplace_listing_id: string;
  domain: string;
  hide_marketplace_scores?: boolean | null;
  claimed_at: string | null;
  claimed_by: string | null;
  status: 'unclaimed' | 'claimed' | 'verified';
  // ENT-6257: the canonical maintainer of the profile (never derived in the browser).
  stewardship?: 'vendor' | 'supply_guard' | null;
  profile_provenance?: 'supplier_attested' | 'supply_guard_managed' | 'public_sources' | null;
  created_at: string;
  updated_at: string;
};

type VendorProfileWithListingRow = Omit<VendorProfileRow, 'domain'> & {
  marketplace_listings?: { domain?: string | null } | Array<{ domain?: string | null }> | null;
};

function getLinkedListingDomain(
  listing: VendorProfileWithListingRow['marketplace_listings'],
) {
  const row = Array.isArray(listing) ? listing[0] : listing;
  return normalizeDomainInput(row?.domain);
}

export async function getAllowedSupplierDomainsForListing(listingId: string): Promise<string[]> {
  const { data: supplierDomainRows, error: supplierDomainError } = await supabase
    .from('supplier_domains')
    .select('domain')
    .eq('marketplace_listing_id', listingId)
    .order('is_primary', { ascending: false });

  if (supplierDomainError) {
    throw supplierDomainError;
  }

  const allowedDomains = new Set(
    ((supplierDomainRows ?? []) as Array<{ domain: string | null }>)
      .map((row) => normalizeDomainInput(row.domain))
      .filter((value): value is string => Boolean(value)),
  );

  const { data: listing, error: listingError } = await supabase
    .from('marketplace_listings')
    .select('domain')
    .eq('id', listingId)
    .maybeSingle();

  if (listingError) {
    throw listingError;
  }

  const canonicalDomain = normalizeDomainInput((listing as { domain?: string | null } | null)?.domain ?? null);
  if (canonicalDomain) {
    allowedDomains.add(canonicalDomain);
  }

  return [...allowedDomains];
}

export async function isSupplierDomainAllowedForListing(listingId: string, domain: string): Promise<boolean> {
  const normalizedDomain = normalizeDomainInput(domain);
  if (!normalizedDomain) {
    return false;
  }

  const allowedDomains = await getAllowedSupplierDomainsForListing(listingId);
  return allowedDomains.includes(normalizedDomain);
}

export async function getSupplierListingIdsForDomain(domain: string): Promise<string[]> {
  const normalizedDomain = normalizeDomainInput(domain);
  if (!normalizedDomain) {
    return [];
  }

  const listingIds = new Set<string>();

  const { data: supplierDomainRows, error: supplierDomainError } = await supabase
    .from('supplier_domains')
    .select('marketplace_listing_id')
    .eq('domain', normalizedDomain);

  if (supplierDomainError) {
    throw supplierDomainError;
  }

  ((supplierDomainRows ?? []) as SupplierDomainRow[]).forEach((row) => {
    if (row.marketplace_listing_id) {
      listingIds.add(row.marketplace_listing_id);
    }
  });

  const { data: listing, error: listingError } = await supabase
    .from('marketplace_listings')
    .select('id')
    .eq('domain', normalizedDomain)
    .maybeSingle();

  if (listingError) {
    throw listingError;
  }

  if ((listing as { id?: string } | null)?.id) {
    listingIds.add((listing as { id: string }).id);
  }

  return [...listingIds];
}

export async function getActiveMarketplaceListingsForDomain(
  domain: string,
  selectClause: string,
): Promise<MarketplaceListingRow[]> {
  const listingIds = await getSupplierListingIdsForDomain(domain);
  if (listingIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('marketplace_listings')
    .select(selectClause)
    .in('id', listingIds)
    .eq('status', 'active');

  if (error) {
    throw error;
  }

  return (data ?? []) as MarketplaceListingRow[];
}

export async function getVendorProfilesForDomain(
  domain: string,
  statuses: Array<'unclaimed' | 'claimed' | 'verified'>,
): Promise<VendorProfileRow[]> {
  const listingIds = await getSupplierListingIdsForDomain(domain);
  if (listingIds.length === 0) {
    return [];
  }

  const { data, error } = await supabase
    .from('vendor_profiles')
    .select(`
      id,
      marketplace_listing_id,
      hide_marketplace_scores,
      claimed_at,
      claimed_by,
      status,
      stewardship,
      profile_provenance,
      created_at,
      updated_at,
      marketplace_listings!inner(domain)
    `)
    .in('marketplace_listing_id', listingIds)
    .in('status', statuses);

  if (error) {
    throw error;
  }

  return ((data ?? []) as VendorProfileWithListingRow[])
    .map((row) => ({
      id: row.id,
      marketplace_listing_id: row.marketplace_listing_id,
      domain: getLinkedListingDomain(row.marketplace_listings) || '',
      hide_marketplace_scores: row.hide_marketplace_scores,
      claimed_at: row.claimed_at,
      claimed_by: row.claimed_by,
      status: row.status,
      stewardship: row.stewardship ?? null,
      profile_provenance: row.profile_provenance ?? null,
      created_at: row.created_at,
      updated_at: row.updated_at,
    }))
    .filter((row) => Boolean(row.domain));
}
